import { useState, type FormEvent } from 'react'
import { NewPassword, type PasswordChangeStatus } from '@rp/contracts'
import { isApiError } from '../../api/client'
import { AuthClientError } from '../../auth/client'
import { Button, Field } from '../../design'
import { PASSWORD_COPY } from './copy'

type Draft = { current: string; next: string; repeat: string }

const EMPTY: Draft = { current: '', next: '', repeat: '' }

type Problem = { field: keyof Draft | null; message: string }

const problemOf = (draft: Draft): Problem | null => {
  if (!NewPassword.safeParse(draft.next).success) return { field: 'next', message: PASSWORD_COPY.tooShort }
  if (draft.next === draft.current) return { field: 'next', message: PASSWORD_COPY.sameAsCurrent }
  if (draft.next !== draft.repeat) return { field: 'repeat', message: PASSWORD_COPY.mismatch }
  return null
}

const failureOf = (error: unknown): Problem => {
  if (error instanceof AuthClientError) {
    return { field: error.kind === 'weak_password' ? 'next' : 'current', message: error.message }
  }
  return { field: null, message: isApiError(error) ? error.message : PASSWORD_COPY.failed }
}

type ChangePasswordFormProps = {
  id: string
  currentLabel: string
  onChange: (currentPassword: string, newPassword: string) => Promise<PasswordChangeStatus>
}

type PasswordInputProps = {
  id: string
  label: string
  hint?: string
  error: string | undefined
  autoComplete: 'current-password' | 'new-password'
  value: string
  onValue: (value: string) => void
}

const PasswordInput = ({ id, label, hint, error, autoComplete, value, onValue }: PasswordInputProps) => (
  <Field id={id} label={label} {...(hint === undefined ? {} : { hint })} {...(error === undefined ? {} : { error })}>
    {(ids) => (
      <input
        id={ids.id}
        type="password"
        autoComplete={autoComplete}
        aria-describedby={ids.describedBy}
        aria-invalid={error === undefined ? undefined : true}
        value={value}
        onChange={(event) => onValue(event.target.value)}
      />
    )}
  </Field>
)

export const ChangePasswordForm = ({ id, currentLabel, onChange }: ChangePasswordFormProps) => {
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [problem, setProblem] = useState<Problem | null>(null)
  const [pending, setPending] = useState(false)
  const [done, setDone] = useState(false)
  const errorFor = (field: keyof Draft) => (problem?.field === field ? problem.message : undefined)

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setDone(false)
    const invalid = problemOf(draft)
    setProblem(invalid)
    if (invalid !== null) return
    setPending(true)
    try {
      const status = await onChange(draft.current, draft.next)
      if (status.passwordChangeRequired) setProblem({ field: null, message: PASSWORD_COPY.notRecorded })
      else setDone(true)
      setDraft(EMPTY)
    } catch (error) {
      setProblem(failureOf(error))
    } finally {
      setPending(false)
    }
  }

  return (
    <form className="password-form" onSubmit={(event) => void submit(event)} noValidate>
      <PasswordInput
        id={`${id}-current`}
        label={currentLabel}
        error={errorFor('current')}
        autoComplete="current-password"
        value={draft.current}
        onValue={(current) => setDraft({ ...draft, current })}
      />
      <PasswordInput
        id={`${id}-next`}
        label={PASSWORD_COPY.next}
        hint={PASSWORD_COPY.nextHint}
        error={errorFor('next')}
        autoComplete="new-password"
        value={draft.next}
        onValue={(next) => setDraft({ ...draft, next })}
      />
      <PasswordInput
        id={`${id}-repeat`}
        label={PASSWORD_COPY.repeat}
        error={errorFor('repeat')}
        autoComplete="new-password"
        value={draft.repeat}
        onValue={(repeat) => setDraft({ ...draft, repeat })}
      />
      {problem?.field === null ? (
        <p className="password-error text-tone tone-danger" role="alert">
          {problem.message}
        </p>
      ) : null}
      {done ? (
        <p className="password-done text-tone tone-success" role="status">
          {PASSWORD_COPY.changed}
        </p>
      ) : null}
      <Button type="submit" variant="primary" pending={pending} pendingLabel={PASSWORD_COPY.submitting}>
        {PASSWORD_COPY.submit}
      </Button>
    </form>
  )
}
